import {
  apiClient,
  idempotencyParams,
  unwrapApiResult,
  type components,
  type operations,
} from '@shared/api';

/** The folded view of the caller's own rights, straight from the contract. */
export type MyPermissions = components['schemas']['MyPermissions'];

/**
 * Pure calls, one per operation — no cache, no state, no notification
 * (`rules/frontend-fsd.mdc` rule 9).
 *
 * `signal` is taken and passed on, unlike the auth calls next door: this one *is* a query, so it is
 * re-issued when its key changes and a stale answer must not overwrite a fresh one
 * (`rules/tanstack-query.mdc` §4).
 */
export const fetchMyPermissions = async (signal?: AbortSignal): Promise<MyPermissions> =>
  unwrapApiResult(await apiClient.GET('/me/permissions', signal === undefined ? {} : { signal }));

/**
 * What **one other person** may do, with the layer of the model that decided each key.
 *
 * The administration counterpart of `MyPermissions` above, and a different shape on purpose: that
 * one is folded to two sets because a guard only needs an answer, this one carries the answer *and*
 * its origin because a screen has to explain it (`permission-model.md` §7(ж)).
 */
export type UserPermissions = components['schemas']['UserPermissions'];

/** One key of the catalogue as it stands for this person: the answer, and who arranged it. */
export type PermissionState = components['schemas']['PermissionState'];

/** The exception in force on one key — reason, author, dates. `null` when there is none. */
export type PermissionOverrideFacts = components['schemas']['PermissionOverrideFacts'];

/** A role the person holds, named as the interface shows it. */
export type HeldRole = components['schemas']['HeldRole'];

/** What writing an exception carries: which way it points, why, and until when. */
export type PermissionOverride = components['schemas']['PermissionOverride'];

/**
 * Every permission of one person, with the origin of each.
 *
 * The signal is required rather than optional, like the other queries here: the key carries the
 * subject, so navigating from one card to another changes it, and a late answer about the previous
 * person must not overwrite a fresh one (`rules/tanstack-query.mdc` §4).
 */
export const fetchUserPermissions = async (
  userId: string,
  signal: AbortSignal,
): Promise<UserPermissions> =>
  unwrapApiResult(
    await apiClient.GET('/users/{userId}/permissions', { params: { path: { userId } }, signal }),
  );

/**
 * Writes the exception on one key — `PUT`, because the pair (person, permission) *is* the row.
 *
 * No `signal`: this is issued by confirming a form, not by a changing query key, so there is no
 * later request that could overtake it — and an option nobody passes is a branch nobody tests.
 *
 * It answers `204` and nothing else, which is why the mutation above it invalidates rather than
 * writing the answer into the cache: the new `source` of the key is a fact only the server can
 * assemble, and guessing it here would be the second permission ladder this screen exists to avoid.
 */
export const writePermissionOverride = async (
  userId: string,
  permission: string,
  override: PermissionOverride,
): Promise<void> => {
  // No `idempotencyParams()`, unlike the two writes next door, and it is not an omission: this
  // operation declares no `Idempotency-Key` parameter (`header?: never` in the generated
  // operation), because `PUT` on the pair (person, permission) is already idempotent — the same
  // exception written twice is the same exception. The middleware still attaches the header to
  // every unsafe request; what would be wrong here is a call site claiming a parameter the contract
  // does not have.
  unwrapApiResult(
    await apiClient.PUT('/users/{userId}/permission-overrides/{permission}', {
      body: override,
      params: { path: { userId, permission } },
    }),
  );
};

/** Removes the exception, putting the person back on whatever their roles say. */
export const removePermissionOverride = async (
  userId: string,
  permission: string,
): Promise<void> => {
  unwrapApiResult(
    await apiClient.DELETE('/users/{userId}/permission-overrides/{permission}', {
      params: { path: { userId, permission } },
    }),
  );
};

/** Every role of the organization with what it grants — the matrix, in one read. */
export type RoleListEntry = components['schemas']['RoleListEntry'];

/** What one drafted change would do, as the server judges it. */
export type RoleChangeOutcome = components['schemas']['RoleChangeOutcome'];

/** A draft: per role, the composition it should end up with. Not a delta. */
export type RoleChanges = components['schemas']['RoleChanges'];

/** The signal is required, not optional: this is a query, and a query is always cancellable. */
export const fetchRoles = async (signal: AbortSignal): Promise<readonly RoleListEntry[]> =>
  unwrapApiResult(await apiClient.GET('/roles', { signal })).items;

/**
 * No signal: this one is issued by pressing Save, not by a changing query key, so there is no later
 * request that could overtake it — and an option nobody passes is a branch nobody tests.
 */
export const previewRoleChanges = async (
  changes: RoleChanges,
): Promise<readonly RoleChangeOutcome[]> =>
  unwrapApiResult(await apiClient.POST('/roles/preview-changes', { body: changes })).items;

/**
 * Saves the draft. `confirmDangerous` repeats a request the server refused with 428 — the header is
 * the confirmation, and the client sends it only after the person has seen what is dangerous.
 */
export const applyRoleChanges = async (
  changes: RoleChanges,
  options: { readonly confirmDangerous?: boolean } = {},
): Promise<void> => {
  const { params } = idempotencyParams();

  unwrapApiResult(
    await apiClient.POST('/roles/apply-changes', {
      body: changes,
      params: {
        header: {
          ...params.header,
          ...(options.confirmDangerous === true ? { 'X-Confirm-Dangerous': '1' as const } : {}),
        },
      },
    }),
  );
};

/** A role of the organization and everything it grants — what `POST /roles` takes. */
export type RoleComposition = components['schemas']['RoleComposition'];

/** A role as stored. Answered by the create, composition included. */
export type Role = components['schemas']['Role'];

/**
 * Composes a role out of the catalogue — `key`, `name`, `description`, `permissions`, and nothing
 * else the endpoint would refuse (`additionalProperties: false`).
 *
 * **No `X-Confirm-Dangerous`, and that is a property of the call rather than an omission.** The
 * server answers `428 confirmation_required` when the composition contains a key the catalogue marks
 * dangerous; this screen creates the role **empty** and the permissions arrive afterwards through
 * `applyRoleChanges`, which does carry the confirmation. A composition with permissions would have
 * to handle the 428 here too — the header exists in the contract for exactly that day.
 *
 * The idempotency key is minted at the call site rather than left to the middleware's default,
 * because the contract marks the header `required` and the generated types therefore demand it here
 * — the spec making «I forgot the header» a compile error instead of a duplicate role.
 *
 * No `signal`: this is issued by submitting a form, not by a changing query key, so there is no
 * later request that could overtake it.
 */
export const createRole = async (composition: RoleComposition): Promise<Role> => {
  const { params } = idempotencyParams();

  return unwrapApiResult(await apiClient.POST('/roles', { body: composition, params }));
};

/** Whom to invite, and what they will hold once they accept. */
export type InvitationDraft = components['schemas']['InvitationDraft'];

/** The answer to creating one: the link, shown exactly once. */
export type MintedInvitation = components['schemas']['MintedInvitation'];

/**
 * Creates an invitation. The response is the only place the link ever exists — the server stores a
 * digest and cannot produce it again.
 *
 * No `signal`: this is issued by pressing a button, not by a changing query key, so there is no
 * later request that could overtake it.
 */
export const createInvitation = async (draft: InvitationDraft): Promise<MintedInvitation> => {
  const { params } = idempotencyParams();

  return unwrapApiResult(await apiClient.POST('/invitations', { body: draft, params }));
};

/**
 * One open invitation as the list carries it — **ids, no names, and no token**.
 *
 * The absence is the contract's, not an omission here: the schema has no `token` and no digest, so
 * the credential exists in exactly one response and this read is not it. What the row does carry is
 * `roleId`, `teamIds` and `invitedById`; turning those into names is a different permission and
 * therefore a different request, made by the screen only when its reader may be told
 * (EPIC-016).
 */
export type Invitation = components['schemas']['Invitation'];

/**
 * Every invitation of the organization that nobody has accepted yet, newest first.
 *
 * No parameters, because the operation takes none: no filter, no page, no order — the order is the
 * server's (`ORDER BY created_at DESC`) and the answer is the whole set. Expired ones are in it on
 * purpose; they are exactly the rows somebody came here to re-issue or close.
 *
 * The `signal` is required rather than optional, like the other queries here: this is a query, and
 * a query is always cancellable (`rules/tanstack-query.mdc` §4).
 */
export const fetchInvitations = async (signal: AbortSignal): Promise<readonly Invitation[]> =>
  unwrapApiResult(await apiClient.GET('/invitations', { signal })).items;

/**
 * Mints a new link for an invitation that already exists, and kills the previous one.
 *
 * No `idempotencyParams()`, and it is not an omission — the same reasoning as
 * `writePermissionOverride` above: this operation declares no `Idempotency-Key` parameter
 * (`header?: never` in the generated operation), so a call site claiming one would be claiming a
 * parameter the contract does not have. The middleware still attaches the header to every unsafe
 * request.
 *
 * No `signal`: it is issued by confirming a dialog, not by a changing query key.
 */
export const resendInvitation = async (invitationId: string): Promise<MintedInvitation> =>
  unwrapApiResult(
    await apiClient.POST('/invitations/{invitationId}/resend', {
      params: { path: { invitationId } },
    }),
  );

/**
 * Closes an invitation early. The row goes, so the link stops working rather than being filtered
 * out by everybody who reads the table.
 *
 * Answers `204` and nothing else, which is why the mutation above it invalidates the list instead
 * of removing the row from the cache: what the list looks like afterwards is the server's answer,
 * and a row deleted optimistically would hide the two refusals that make this operation
 * interesting — `404` for one already closed and `409` for one already accepted.
 */
export const revokeInvitation = async (invitationId: string): Promise<void> => {
  unwrapApiResult(
    await apiClient.DELETE('/invitations/{invitationId}', {
      params: { path: { invitationId } },
    }),
  );
};

/** What an ownership transfer changed: who held it, who holds it, and what the previous one kept. */
export type OwnershipTransferResult =
  operations['transferOwnership']['responses']['200']['content']['application/json'];

/**
 * What the outgoing owner keeps, and the only value this client sends.
 *
 * **The choice the contract asks the screen to show cannot be shown, and this is why.** The
 * operation's description says «`previousOwnerRoleKey` defaults to `admin` and the screen shows the
 * choice in the confirmation», and the published enum offers `admin | manager | lead | developer |
 * viewer`. The server accepts a different set — `admin | head_of_legal | lawyer | assistant | clerk |
 * compliance | auditor` (`presentation/http/validators/ownership.validator.ts`), which is the product's
 * actual system roles minus `owner`. The two lists intersect in exactly one value, and it is this one.
 *
 * So four of the five values the generated type permits are values the server answers `422` to, and
 * three of the seven the server accepts cannot be typed at this call site at all. Offering a select
 * built from the generated union would be offering four options that always fail. Until the spec and
 * the validator agree, the outgoing owner keeps `admin` — the default both sides do share — and the
 * dialog says so in words instead of drawing a control over a broken enum.
 *
 * Sent explicitly rather than left to either default, so that what this client asked for is visible
 * on the wire and assertable in a test, instead of being a property of two defaults agreeing.
 */
const PREVIOUS_OWNER_ROLE_KEY = 'admin' as const;

/**
 * Hand the organization to somebody else.
 *
 * No `signal`: issued by confirming a dialog, so there is no later request that could overtake it —
 * and a cancelled transfer is worse than a slow one, because the caller would never learn whether
 * the organization changed hands.
 *
 * `toUserId` and nothing else in the body. The story behind this operation asked the outgoing owner
 * to type the organization's `slug` back; the server's schema is strict and answers `422` to a
 * `confirmation` field, deliberately — «a server-side comparison would add nothing an attacker
 * holding the token could not satisfy, while making the operation impossible to script». The control
 * therefore lives entirely in the dialog.
 */
export const transferOwnership = async (toUserId: string): Promise<OwnershipTransferResult> => {
  // Minted per call rather than left to the middleware's default, because the contract declares the
  // parameter on this operation and the generated types therefore demand it at the call site — the
  // spec making «I forgot the header» a compile error instead of a second transfer.
  const { params } = idempotencyParams();

  return unwrapApiResult(
    await apiClient.POST('/organization/transfer-ownership', {
      params,
      body: { toUserId, previousOwnerRoleKey: PREVIOUS_OWNER_ROLE_KEY },
    }),
  );
};
